import { useEffect, useRef } from "react";

export default function PlugAndBarSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          section.classList.add("opacity-100");
          section.classList.remove("opacity-0");
        }
      },
      { threshold: 0.08 },
    );
    observer.observe(section);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const items = content.querySelectorAll<HTMLElement>(".fade-up-item");
    const itemObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            itemObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" },
    );
    items.forEach((item) => itemObserver.observe(item));

    return () => itemObserver.disconnect();
  }, []);

  return (
    <section
      id="plugandbar"
      ref={sectionRef}
      className="relative opacity-0 transition-opacity duration-1000 bg-gradient-coral"
    >
      {/* Subtle depth overlay for the gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/10 pointer-events-none" />

      <div className="relative py-14 md:py-24 overflow-hidden">
        <div ref={contentRef} className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-0">
            {/* LEFT: Text content in premium white card */}
            <div className="lg:w-[46%] relative z-20 lg:-mr-14 xl:-mr-20">
              <div className="bg-white border border-background-200/50 rounded-2xl p-7 md:p-10 lg:p-11 shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
                <span className="fade-up-item inline-block text-[10px] md:text-[11px] tracking-[0.32em] uppercase text-foreground-900 font-light mb-4">
                  Complétez votre événement avec :
                </span>

                <div className="fade-up-item delay-1 mb-3">
                  <img
                    src="https://storage.readdy-site.link/project_files/90a975e3-fa94-47ff-988b-c34c350fc03a/bc083093-2f20-4016-a611-0d1d28473289_plug-and-bar.png?v=917509636eff9f4c4c7c9450351ff3b8"
                    alt="Plug and Bar logo"
                    className="w-full max-w-[280px] h-auto object-contain"
                  />
                </div>

                <p className="fade-up-item delay-1 text-base md:text-lg text-foreground-700 font-medium mb-5 leading-snug">
                  Une solution mobile clé en main pensée assurer un service
                  fluide et rapide même en forte affluence.
                </p>

                <div className="fade-up-item delay-2 w-12 h-px bg-primary-200/50 mb-5" />

                <div className="space-y-3.5 mb-7">
                  <p className="fade-up-item delay-2 text-[15px] md:text-base text-foreground-800 leading-[1.8]">
                    Plug and Bar est un concept de remorques-bars mobiles conçu
                    pour maximiser le rendement grâce à un équipement
                    entièrement intégré.
                  </p>

                  <ul className="fade-up-item delay-3 space-y-2.5 pt-1">
                    <li className="flex items-start gap-3 text-[15px] md:text-base text-foreground-800 leading-snug">
                      <span className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                        <i className="ri-check-line text-lg text-terracotta" />
                      </span>
                      Jusqu&apos;à 1&rsquo;000 bières servies par heure
                    </li>
                    <li className="flex items-start gap-3 text-[15px] md:text-base text-foreground-800 leading-snug">
                      <span className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                        <i className="ri-check-line text-lg text-terracotta" />
                      </span>
                      Grande capacité de stockage pour un service fluide et sans
                      interruption
                    </li>
                    <li className="flex items-start gap-3 text-[15px] md:text-base text-foreground-800 leading-snug">
                      <span className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                        <i className="ri-check-line text-lg text-terracotta" />
                      </span>
                      Installation rapide en 15 minutes, prête à l&apos;emploi
                    </li>
                  </ul>
                </div>

                <div className="fade-up-item delay-4">
                  <a
                    href="https://plugandbar.ch"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full text-white font-semibold text-sm md:text-base transition-all duration-500 whitespace-nowrap cursor-pointer bg-[#8B3B3B] shadow-[0_4px_20px_rgba(139,59,59,0.25)] hover:bg-[#7A2E2E] hover:-translate-y-0.5 hover:shadow-[0_10px_35px_rgba(139,59,59,0.35)]"
                  >
                    <span className="w-4 h-4 flex items-center justify-center transition-transform duration-500 group-hover:translate-x-0.5">
                      <i className="ri-arrow-right-line text-lg" />
                    </span>
                    Découvrir Plug and Bar
                  </a>
                </div>
              </div>
            </div>

            {/* RIGHT: Premium image */}
            <div className="lg:w-[58%] relative z-10">
              <div className="relative rounded-2xl overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.08)] group">
                <img
                  src="https://static.readdy.ai/image/254cde0d10dfb5832a5e0a0044c661fc/7594e4f1bfbe837fbe40b448ca0de2e1.png"
                  alt="Plug & Bar — remorque bar mobile en situation lors d'un événement"
                  className="w-full aspect-[4/5] object-cover object-center transition-transform duration-700 group-hover:scale-[1.02]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent pointer-events-none" />
                <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-white/20 backdrop-blur-sm" />
              </div>

              {/* Decorative dot pattern behind image */}
              <div className="hidden lg:block absolute -top-6 -right-6 w-20 h-20 pointer-events-none">
                <div className="grid grid-cols-4 gap-1.5 opacity-10">
                  {Array.from({ length: 16 }).map((_, i) => (
                    <div key={i} className="w-1.5 h-1.5 rounded-full bg-foreground-900" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}